import React from 'react';
import { Badge, Button, Heading, Layout, Text } from '../../../components/core';
import { IconClockFour } from '../../../components/icons/IconClockFour';
import { IconCoffee } from '../../../components/icons/IconCoffee';
import { IconCompass } from '../../../components/icons/IconCompass';
import { IconHeart } from '../../../components/icons/IconHeart';
import { IconHeartHandShake } from '../../../components/icons/IconHeartHandShake';
import { IconHome } from '../../../components/icons/IconHome';
import { IconMoon } from '../../../components/icons/IconMoon';
import { IconNavigation } from '../../../components/icons/IconNavigation';
import { IconPin } from '../../../components/icons/IconPin';
import { IconSprout } from '../../../components/icons/IconSprout';
import { IconTrophy } from '../../../components/icons/IconTrophy';
import { PageError } from '../../../components/page';
import { useNavigateOnPortal } from '../../../content/portal/usePortalNavigate';
import { useTherapyContext } from '../../../contexts';
import { useBreakpointSwitch } from '../../../contexts/breakpointContext';
import {
  useConsumeCoachingRecommendationMutation,
  useGetHubAssessmentLinkQuery,
  useConsumeTherapyRecommendationMutation,
} from '../../../graphQL';
import { getRoute, useNavigate, useExternalNavigate, useCurrentRouteName } from '../../../routes';
import { getStylesheet } from '../../../styles';
import { ImageCoachingStudent } from '../images/ImageCoachingStudent';
import { ImageCoachingStudentSquare } from '../images/ImageCoachingStudentSquare';
import { ImageHandPhone } from '../images/ImageHandPhone';
import { ImageHandPhoneSquare } from '../images/ImageHandPhoneSquare';
import { ImageHandPlant } from '../images/ImageHandPlant';
import { ImageHandPlantSquare } from '../images/ImageHandPlantSquare';
import { ImageHandTablet } from '../images/ImageHandTablet';
import { ImageHandTabletSquare } from '../images/ImageHandTabletSquare';

type CareRecommendationCTACardProps = {
  showRecommended?: boolean;
  title: string;
  Image: () => JSX.Element;
  SquareImage: () => JSX.Element;
  caption: string;
  features: JSX.Element[];
  cta: JSX.Element;
};

export const CareRecommendationCTACard = ({
  showRecommended = false,
  title,
  Image,
  SquareImage,
  caption,
  features,
  cta,
}: CareRecommendationCTACardProps): JSX.Element => {
  const [useSquareImage] = useBreakpointSwitch({
    mobile: [true],
    desktop: [false],
  });

  return (
    <Layout.Shift {...styles.careRecommendationCard}>
      <Layout.Stack {...styles.CTAImageStyles}>
        {useSquareImage ? <SquareImage /> : <Image />}
      </Layout.Stack>
      <Layout.VStack {...styles.careRecommendationCardContent} space={5}>
        <Layout.VStack space={5}>
          {showRecommended && (
            <Badge.successSubtle {...styles.recommendedBadge}>Recommended</Badge.successSubtle>
          )}
          <Heading.h3>{title}</Heading.h3>
          <Text.paraSmall {...styles.caption}>{caption}</Text.paraSmall>
          <Layout.VStack space={3}>
            {features.map((feature, index) => {
              return (
                <Layout.HStack key={index} space={3}>
                  {feature}
                </Layout.HStack>
              );
            })}
          </Layout.VStack>
        </Layout.VStack>
        <Layout.VStack space={2}>{cta}</Layout.VStack>
      </Layout.VStack>
    </Layout.Shift>
  );
};

const styles = getStylesheet({
  CTAImageStyles: {
    alignSelf: 'flex-start',
    borderRadius: 8,
    overflow: 'hidden',
  },

  careRecommendationCard: {
    backgroundColor: 'secondary.50',
    borderRadius: 4,
    gap: 9,
    padding: 6,
    shadow: 'large',
  },

  careRecommendationCardContent: {
    flex: 1,
    flexGrow: 1,
  },

  careRecommendationCTAButton: {
    alignSelf: 'flex-start',
  },

  caption: {
    maxWidth: '95%',
  },

  recommendedBadge: {
    alignSelf: 'flex-start',
  },
});

export const TherapyCTACard = (): JSX.Element => {
  const [consumeTherapyRecommendation, { loading }] = useConsumeTherapyRecommendationMutation();

  const { navigateToPatientPortal } = useTherapyContext();

  const onTherapyCTAClick = async (): Promise<void> => {
    const response = await consumeTherapyRecommendation();
    if (response.data?.consumeTherapyRecommendation === true) {
      await navigateToPatientPortal();
    }
  };

  return (
    <CareRecommendationCTACard
      showRecommended={true}
      title="Individual Therapy"
      Image={ImageHandPlant}
      SquareImage={ImageHandPlantSquare}
      caption="Schedule an initial visit to get started with care. Select from a list of specialized providers and pick a date and time that works best for you."
      features={[
        <>
          <IconHome size={4} accessibilityHidden />
          <Text.caption>Virtual care from the comfort of your home</Text.caption>
        </>,
        <>
          <IconHeart size={4} accessibilityHidden />
          <Text.caption>Meet your needs and preferences</Text.caption>
        </>,
        <>
          <IconMoon size={4} accessibilityHidden />
          <Text.caption>Available nights and weekends</Text.caption>
        </>,
      ]}
      cta={
        <Button.primaryMedium
          {...styles.careRecommendationCTAButton}
          testID="button-therapy-recommendation-cta"
          onPress={onTherapyCTAClick}
          isLoading={loading}
        >
          Choose a therapist
        </Button.primaryMedium>
      }
      key="therapy-cta-card"
    />
  );
};

export const EmotionalWellnessCoachingCTACard = (): JSX.Element => {
  const [consumeCoachingRecommendation, { loading }] = useConsumeCoachingRecommendationMutation();

  const navigateOnPortal = useNavigateOnPortal();
  const currentRoute = useCurrentRouteName();

  const onCoachingCTAClick = async (): Promise<void> => {
    const response = await consumeCoachingRecommendation();

    if (response.data?.consumeCoachingRecommendation === true) {
      navigateOnPortal(getRoute('coachingOnboarding', {}), { state: { from: currentRoute } });
    }
  };

  return (
    <CareRecommendationCTACard
      title="Emotional Wellness Coaching"
      Image={ImageCoachingStudent}
      SquareImage={ImageCoachingStudentSquare}
      caption="Connect with a coach who can help you achieve your wellness goals. Your coach will collaborate with you to create a personalized plan to help reach your individual goals around wellness topics such as anxiety, confidence and communication."
      features={[
        <>
          <IconHeartHandShake size={4} accessibilityHidden />
          <Text.caption>Diverse, professional coaches</Text.caption>
        </>,
        <>
          <IconTrophy size={4} accessibilityHidden />
          <Text.caption>Tackle your wellness goals</Text.caption>
        </>,
        <>
          <IconClockFour size={4} accessibilityHidden />
          <Text.caption>24/7 access to evidence-based content</Text.caption>
        </>,
      ]}
      cta={
        <Button.primaryMedium
          {...styles.careRecommendationCTAButton}
          testID="button-coaching-recommendation-cta"
          onPress={onCoachingCTAClick}
          isLoading={loading}
        >
          Continue with coaching
        </Button.primaryMedium>
      }
      key="coaching-cta-card"
    />
  );
};

export const SelfCareCTACard = (): JSX.Element => {
  const navigate = useNavigate();
  return (
    <CareRecommendationCTACard
      title="Self Care"
      Image={ImageHandPhone}
      SquareImage={ImageHandPhoneSquare}
      caption="Build essential life skills that can help you thrive on and off campus. This self-guided, evidence-based program was created specifically for college students and will help you take charge of your well-being and teach you how to cope, problem-solve, and communicate."
      features={[
        <>
          <IconClockFour size={4} accessibilityHidden />
          <Text.caption>24/7 access to all content</Text.caption>
        </>,
        <>
          <IconNavigation size={4} accessibilityHidden />
          <Text.caption>Complete at your own pace</Text.caption>
        </>,
        <>
          <IconSprout size={4} accessibilityHidden />
          <Text.caption>Short, dynamic micro-lessons</Text.caption>
        </>,
      ]}
      cta={
        <Button.primaryMedium
          {...styles.careRecommendationCTAButton}
          testID="button-self-care-recommendation-cta"
          onPress={() => navigate(getRoute('selfCareHome', {}))}
        >
          Explore Self Care
        </Button.primaryMedium>
      }
      key="self-care-cta-card"
    />
  );
};

export const AssessmentVisitCTACard = (): JSX.Element | null => {
  const externalNavigate = useExternalNavigate();

  const { data: linkData, error, refetch } = useGetHubAssessmentLinkQuery();

  if (!linkData || error) {
    return <PageError onRefreshPress={refetch} />;
  }

  return (
    <CareRecommendationCTACard
      showRecommended={true}
      title="Schedule an assessment visit"
      Image={ImageHandTablet}
      SquareImage={ImageHandTabletSquare}
      caption="Book a short meeting with a member of our team to further discuss your needs and we'll ensure you're connected to the right level of care."
      features={[
        <>
          <IconCoffee size={4} accessibilityHidden />
          <Text.caption>Meet with a professional</Text.caption>
        </>,
        <>
          <IconPin size={4} accessibilityHidden />
          <Text.caption>Discuss your needs</Text.caption>
        </>,
        <>
          <IconCompass size={4} accessibilityHidden />
          <Text.caption>Connect with the right care</Text.caption>
        </>,
      ]}
      cta={
        <Button.primaryMedium
          {...styles.careRecommendationCTAButton}
          testID="button-assessment-visit-recommendation-cta"
          onPress={() => externalNavigate(linkData.hubGetAssessmentLink)}
        >
          Schedule Assessment Visit
        </Button.primaryMedium>
      }
      key="assessment-visit-cta-card"
    />
  );
};
